import { format, parseISO } from 'date-fns';
import { FileText, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  useDocuments,
  useIdentityStatus,
  useUploadDocument,
} from '@/lib/api/hooks';
import { Badge, Button, Card } from '@/design-system/primitives';

const purposeTone: Record<string, 'accent' | 'success' | 'info' | 'neutral'> = {
  lab: 'info',
  prescription: 'accent',
  id: 'neutral',
  other: 'neutral',
};

function humanBytes(bytes: number): string | null {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentsTab() {
  const { data, isLoading } = useDocuments();
  const upload = useUploadDocument();
  const identity = useIdentityStatus();
  const idAlreadyReceived =
    identity.data?.enabled &&
    identity.data.status === 'on_file';
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // When the home Upload-Government-ID task routes here with ?upload=id,
  // pre-set the upload purpose and auto-open the picker on mount so the
  // patient lands directly in the file sheet. Latched to local state so
  // clearing the URL param doesn't reset the purpose mid-flight.
  const [pendingPurpose, setPendingPurpose] = useState<string>('other');

  const handlePick = () => inputRef.current?.click();

  useEffect(() => {
    if (searchParams.get('upload') !== 'id' || identity.isLoading) return;
    if (!idAlreadyReceived) {
      setPendingPurpose('id');
      handlePick();
    }
    // Clear the query param so a refresh doesn't re-pop the picker.
    const next = new URLSearchParams(searchParams);
    next.delete('upload');
    setSearchParams(next, { replace: true });
    // Intentionally one-shot on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity.isLoading, idAlreadyReceived, searchParams, setSearchParams]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setFeedback(null);
    try {
      const result = await upload.mutateAsync({
        file,
        purpose: pendingPurpose,
      });
      setFeedback(result.feedback ?? null);
    } catch (err) {
      setError((err as Error).message ?? 'Upload failed');
    } finally {
      // Reset so picking the same file twice still triggers onChange.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  if (isLoading) return <div className="h-[20rem] bg-sunken animate-pulse" />;
  const docs = data ?? [];

  return (
    <section className="flex flex-col gap-20">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleChange}
        // Cap matches the backend's 50 MB ceiling. Browser still allows
        // larger picks; the backend rejects in that case.
        accept="*/*"
      />
      <div className="flex items-center justify-between gap-10">
        <div>
          <h2 className="type-label-md">All documents</h2>
          <p className="type-body-sm text-ink-muted mt-5">
            Labs, prescriptions, and anything shared with your clinician.
          </p>
          {pendingPurpose === 'id' && !idAlreadyReceived && (
            <p className="type-body-sm text-ink-secondary mt-5">
              Upload the complete front of your government ID or passport photo
              page, with your photo, full name, and date of birth readable. The
              back alone is not sufficient.
            </p>
          )}
        </div>
        <Button size="sm" onClick={handlePick} loading={upload.isPending}>
          <Upload size={14} strokeWidth={1.5} /> Upload
        </Button>
      </div>

      {error && (
        <div role="alert" className="type-body-sm text-danger">
          {error}
        </div>
      )}
      {feedback && (
        <div role="status" className="type-body-sm text-ink-secondary">
          {feedback}
        </div>
      )}

      {docs.length === 0 ? (
        <Card padding="lg">
          <div className="flex flex-col items-center text-center gap-10 py-20">
            <FileText size={20} className="text-ink-muted" />
            <div className="type-label-md text-ink-muted">No documents yet</div>
            <p className="type-body-sm text-ink-secondary max-w-[48ch]">
              Upload lab results, prescriptions, or anything your clinician
              should see.
            </p>
          </div>
        </Card>
      ) : (
        <ul className="flex flex-col gap-10">
          {docs.map((d) => {
            const uploaded = parseISO(d.uploaded_at);
            return (
              <li
                key={d.id}
                className="flex items-center gap-15 p-15 border border-line-subtle bg-raised hover:border-line-DEFAULT flex-wrap sm:flex-nowrap"
              >
                <FileText
                  size={18}
                  strokeWidth={1.5}
                  className="text-ink-secondary shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="type-title-sm truncate">{d.filename}</div>
                  <div className="type-body-sm text-ink-muted mt-5 flex items-center gap-10 flex-wrap">
                    {humanBytes(d.size_bytes) && (
                      <>
                        <span className="type-data-sm">
                          {humanBytes(d.size_bytes)}
                        </span>
                        <span aria-hidden>·</span>
                      </>
                    )}
                    <span>Uploaded {format(uploaded, 'MMM d, yyyy')}</span>
                  </div>
                </div>
                <Badge tone={purposeTone[d.purpose] ?? 'neutral'} size="sm">
                  {d.purpose}
                </Badge>
                {d.download_url && (
                  <Button variant="ghost" size="sm" asChild>
                    <a href={d.download_url} target="_blank" rel="noreferrer">
                      Download
                    </a>
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
